/**
 * Unified Database Browse Page
 * 
 * Single page component for browsing all database entity types.
 * Uses configurable layouts (table, cards, groupedCards) per entity type.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
    Search, AlertTriangle, X, Filter, ArrowUpDown,
    Heart, Activity, Users, Package, MapPin, Zap, Sparkles,
    LayoutList, LayoutGrid, FolderOpen, ChevronDown, ChevronRight,
    User, Move, Fingerprint, Link2, Gamepad2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevMode } from '../contexts/DevModeContext';
import { useCollection } from '../contexts/CollectionContext';
import api from '../services/api';
import DatabaseHero from '../components/DatabaseHero';
import { Spinner, Pulse, Float } from '../components/Animations';
import { APPEAL_FACETS, rankAppealLenses, getAppealProfileHighlights, describeAppealProfile } from '../utils/appealLenses';

import ArchetypeCard from '../components/ArchetypeCard';
import { stripMarkdown } from '../utils/stripMarkdown';
import { DATABASE_BROWSE_RESET_DEFAULTS, deriveKinkBrowseSplitState } from './databaseBrowsePage.logic';
import { shouldLoadNextKinkPage, shouldRevealArchiveBoundary } from './databaseBrowsePage.triggers';

// ============================================================================
// ENTITY CONFIGURATIONS
// ============================================================================

const ENTITY_CONFIG = {
    kinks: {
        apiMethod: 'getKinks',
        apiParams: {},
        icon: Heart,
        label: 'Explore Desire',
        title: 'Explore Kinks',
        highlightWord: 'Kinks',
        description: 'Explore the full spectrum of desires, fantasies, and practices.',
        layout: 'cards',
        detailRoute: '/kink',
        statsConfig: (data, categories, totalItems) => [
            { value: totalItems || data?.length || 0, label: 'Entries' },
            { value: categories?.length || 0, label: 'Categories' }
        ]
    },
    actions: {
        apiMethod: 'getSexActions',
        icon: Activity,
        label: 'The Art of Pleasure',
        title: 'Sex Acts & Techniques',
        highlightWord: 'Techniques',
        description: 'Master positions, techniques, and practices from foundational to adventurous.',
        layout: 'cards',
        detailRoute: '/action',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Techniques' }
        ]
    },
    archetypes: {
        apiMethod: 'getArchetypes',
        icon: Users,
        label: 'Know Yourself',
        title: 'Sexual Archetypes',
        highlightWord: 'Archetypes',
        description: 'Discover the psychological patterns and identities that shape who you are in intimate spaces.',
        layout: 'cards',
        detailRoute: '/archetype',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Archetypes' }
        ]
    },
    gear: {
        apiMethod: 'getGear',
        icon: Package,
        label: 'Equipment',
        title: 'Gear & Equipment',
        highlightWord: 'Equipment',
        description: 'Explore toys, restraints, accessories, and equipment.',
        layout: 'cards',
        detailRoute: '/gear',
        statsConfig: (data, categories) => [
            { value: data?.length || 0, label: 'Items' },
            { value: categories?.length || 0, label: 'Categories' }
        ]
    },
    scenarios: {
        apiMethod: 'getScenarios',
        icon: MapPin,
        label: 'Imagination Engine',
        title: 'Sexual Scenarios',
        highlightWord: 'Scenarios',
        description: 'Explore fantasies across every setting and intensity level.',
        layout: 'cards',
        detailRoute: '/scenario',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Scenarios' }
        ]
    },
    mechanics: {
        apiMethod: 'getArousalMechanics',
        icon: Zap,
        label: 'What Turns You On',
        title: 'Arousal Sources',
        highlightWord: 'Sources',
        description: 'Understand the psychological and physical triggers that drive arousal.',
        layout: 'cards',
        detailRoute: '/mechanic',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Sources' }
        ]
    },
    effects: {
        apiMethod: 'getArousalEffects',
        icon: Sparkles,
        label: 'How It Feels',
        title: 'Arousal Effects',
        highlightWord: 'Effects',
        description: 'Discover the sensations, emotions, and physical responses that arousal produces.',
        layout: 'cards',
        detailRoute: '/effect',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Effects' }
        ]
    },
    characters: {
        apiMethod: 'getCharacters',
        responseKey: 'characters',
        icon: User,
        label: 'Anime & Game Characters',
        title: 'Characters',
        highlightWord: 'Characters',
        description: 'Find anime and game characters with sourced references.',
        layout: 'cards',
        detailRoute: '/character',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Characters' }
        ]
    },
    positions: {
        apiMethod: 'getPositions',
        responseKey: 'positions',
        icon: Move,
        label: 'Sex Positions',
        title: 'Positions',
        highlightWord: 'Positions',
        description: 'Explore positions by difficulty, flexibility requirements, and intensity.',
        layout: 'cards',
        detailRoute: '/position',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Positions' }
        ]
    },
    bodyParts: {
        apiMethod: 'getBodyParts',
        responseKey: 'bodyParts',
        icon: Fingerprint,
        label: 'Erogenous Zones',
        title: 'Body Parts',
        highlightWord: 'Parts',
        description: 'Map sensitivity, stimulation techniques, and common reactions.',
        layout: 'cards',
        detailRoute: '/body-part',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Body Parts' }
        ]
    },
    dynamics: {
        apiMethod: 'getDynamics',
        responseKey: 'dynamics',
        icon: Link2,
        label: 'Power & Relationship',
        title: 'Dynamics',
        highlightWord: 'Dynamics',
        description: 'Understand power exchange, relationship structures, and communication needs.',
        layout: 'cards',
        detailRoute: '/dynamic',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Dynamics' }
        ]
    },
    activities: {
        apiMethod: 'getActivities',
        responseKey: 'activities',
        icon: Gamepad2,
        label: 'Bedroom Activities',
        title: 'Activities',
        highlightWord: 'Activities',
        description: 'Explore games, challenges, and activities to spice up intimate encounters.',
        layout: 'cards',
        detailRoute: '/activity',
        statsConfig: (data) => [
            { value: data?.length || 0, label: 'Activities' }
        ]
    }
};

// Valid entity types for routing
const VALID_ENTITIES = Object.keys(ENTITY_CONFIG);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function DatabaseBrowsePage() {
    const { entityType } = useParams();
    const { isDevMode } = useDevMode();

    // Redirect invalid entity types
    if (!VALID_ENTITIES.includes(entityType)) {
        return <Navigate to="/db/kinks" replace />;
    }

    const config = ENTITY_CONFIG[entityType];
    const Icon = config.icon;

    // State
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState(config.layout === 'cards' ? 'card' : 'table');
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalItems, setTotalItems] = useState(0); // Total count from DB
    const [intensityCounts, setIntensityCounts] = useState({}); // Backend-provided intensity distribution
    const [categoryCounts, setCategoryCounts] = useState({}); // Backend-provided category counts
    const [initialPopularCount, setInitialPopularCount] = useState(null);
    const [paginationLimit, setPaginationLimit] = useState(config.apiParams?.limit || null);

    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);
    const archiveRevealRef = useRef(null);
    const popularSectionRef = useRef(null);
    const loadMoreLockRef = useRef(false);

    // Multi-dimensional filter state
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedIntensities, setSelectedIntensities] = useState([]); // Array for multi-select: ['mild', 'moderate', etc.]
    const [selectedLenses, setSelectedLenses] = useState([]);
    const [sortBy, setSortBy] = useState('name'); // 'name', 'weirdnessScore', 'rarity'
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);
    const [showNiche, setShowNiche] = useState(false);
    const [showArchive, setShowArchive] = useState(false);

    // Intensity level definitions (maps to weirdnessScore ranges 1-100)
    const INTENSITY_LEVELS = [
        { key: 'mild', label: 'Mild', range: [0, 25], color: 'emerald' },
        { key: 'moderate', label: 'Moderate', range: [26, 50], color: 'amber' },
        { key: 'intense', label: 'Intense', range: [51, 75], color: 'orange' },
        { key: 'extreme', label: 'Extreme', range: [76, 100], color: 'wine' }
    ];

    // Collapsed groups for groupedCards layout
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    // Extract unique categories from data
    const uniqueCategories = useMemo(() => {
        const cats = [...new Set(data.map(item => item.category).filter(Boolean))];
        return cats.sort();
    }, [data]);

    // Note: intensityCounts and categoryCounts now come from backend stats endpoint

    // Load data on entity type change
    useEffect(() => {
        async function loadData() {
            setLoading(true);
            setError(null);
            setData([]);
            setCurrentPage(1);
            setInitialPopularCount(null);
            setPaginationLimit(null);
            setSearchQuery(DATABASE_BROWSE_RESET_DEFAULTS.searchQuery);
            setSelectedCategory(DATABASE_BROWSE_RESET_DEFAULTS.selectedCategory);
            setSelectedIntensities(DATABASE_BROWSE_RESET_DEFAULTS.selectedIntensities);
            setSelectedLenses(DATABASE_BROWSE_RESET_DEFAULTS.selectedLenses);
            setShowNiche(DATABASE_BROWSE_RESET_DEFAULTS.showNiche);
            setShowArchive(DATABASE_BROWSE_RESET_DEFAULTS.showArchive);

            try {
                // For kinks, use pagination - fetch page 1
                const params = entityType === 'kinks'
                    ? { page: 1 }
                    : { ...(config.apiParams || {}) };
                const result = await api[config.apiMethod](params);

                // Handle different API response shapes
                if (Array.isArray(result)) {
                    setData(result);
                    setHasMore(false);
                } else if (result.kinks) {
                    setData(result.kinks);
                    setInitialPopularCount(result.kinks.length);
                    setHasMore(result.pagination?.currentPage < result.pagination?.totalPages);
                    setCurrentPage(result.pagination?.currentPage || 1);

                    if (result.pagination?.itemsPerPage) {
                        setPaginationLimit(result.pagination.itemsPerPage);
                    }

                    // Fetch full DB stats (intensity distribution, category counts, total)
                    try {
                        const stats = await api.getStats();
                        console.log('Stats response:', stats);
                        setTotalItems(result.pagination?.totalItems || stats.totalItems || result.kinks.length);
                        setIntensityCounts(stats.intensityCounts || {});
                        setCategoryCounts(stats.categoryCounts || {});
                        if (stats.categories && Array.isArray(stats.categories)) {
                            setCategories(stats.categories);
                        }
                    } catch (e) {
                        console.warn('Failed to fetch stats:', e);
                        // Fallback to pagination totalItems
                        setTotalItems(result.pagination?.totalItems || result.kinks.length);
                    }
                } else if (result.actions) {
                    setData(result.actions);
                    setHasMore(false);
                } else if (result.gear) {
                    setData(result.gear);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (result.scenarios) {
                    setData(result.scenarios);
                    setHasMore(false);
                } else if (result.mechanics || result.mechanisms) {
                    setData(result.mechanics || result.mechanisms);
                    setHasMore(false);
                } else if (result.effects) {
                    setData(result.effects);
                    setHasMore(false);
                } else if (result.positions) {
                    setData(result.positions);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (result.bodyParts) {
                    setData(result.bodyParts);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (result.dynamics) {
                    setData(result.dynamics);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (result.characters) {
                    setData(result.characters);
                    if (result.sourceTypes) setCategories(result.sourceTypes);
                    setHasMore(false);
                } else if (result.activities) {
                    setData(result.activities);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (config.responseKey && result[config.responseKey]) {
                    // Generic handler using config.responseKey
                    setData(result[config.responseKey]);
                    if (result.categories) setCategories(result.categories);
                    setHasMore(false);
                } else if (Array.isArray(result)) {
                    // Fallback for array responses
                    setData(result);
                    setHasMore(false);
                } else {
                    // Last resort - assume empty
                    console.warn('Unexpected API response shape:', result);
                    setData([]);
                    setHasMore(false);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [entityType, config.apiMethod]);

    // Load more data for infinite scroll (kinks only for now)
    const loadMore = useCallback(async () => {
        if (loadMoreLockRef.current || loadingMore || !hasMore || entityType !== 'kinks') return;

        loadMoreLockRef.current = true;
        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const params = {
                ...(config.apiParams || {}),
                ...(paginationLimit ? { limit: paginationLimit } : {}),
                page: nextPage
            };
            const result = await api[config.apiMethod](params);

            if (result.kinks && result.kinks.length > 0) {
                setData(prev => [...prev, ...result.kinks]);
                setCurrentPage(nextPage);
                setHasMore(result.pagination?.currentPage < result.pagination?.totalPages);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading more:', err);
        } finally {
            loadMoreLockRef.current = false;
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, currentPage, entityType, config.apiMethod, config.apiParams, paginationLimit]);

    // Search, category, and intensity filtering with sorting (client-side)
    const baseFilteredData = useMemo(() => {
        let filtered = data;

        // Apply category filter
        if (selectedCategory) {
            filtered = filtered.filter(item => item.category === selectedCategory);
        }

        // Apply intensity filter (multi-select OR logic)
        if (selectedIntensities.length > 0) {
            filtered = filtered.filter(item => {
                const score = item.weirdnessScore || 0;
                return selectedIntensities.some(key => {
                    const level = INTENSITY_LEVELS.find(l => l.key === key);
                    return level && score >= level.range[0] && score <= level.range[1];
                });
            });
        }

        // Apply appeal filter for kinks only (matches strongest appeal signals)
        if (entityType === 'kinks' && selectedLenses.length > 0) {
            filtered = filtered.filter(item => {
                const topLensKeys = rankAppealLenses(item).slice(0, 3).map(facet => facet.key);
                return selectedLenses.some(key => topLensKeys.includes(key));
            });
        }

        // Apply text search
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                (item.name || item.scenarioDescription || '')?.toLowerCase().includes(query) ||
                item.category?.toLowerCase().includes(query) ||
                item.description?.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        filtered = [...filtered].sort((a, b) => {
            let aVal, bVal;
            switch (sortBy) {
                case 'weirdnessScore':
                    aVal = a.weirdnessScore || 0;
                    bVal = b.weirdnessScore || 0;
                    break;
                case 'rarity':
                    aVal = a.rarity || 0;
                    bVal = b.rarity || 0;
                    break;
                case 'sensoryAppeal':
                case 'auditoryAppeal':
                case 'psychologicalAppeal':
                case 'tabooAppeal':
                case 'powerAppeal':
                case 'intimacyAppeal':
                case 'humiliationAppeal':
                case 'exposureAppeal':
                    aVal = a[sortBy] || 0;
                    bVal = b[sortBy] || 0;
                    break;
                default:
                    aVal = (a.name || a.scenarioDescription || '').toLowerCase();
                    bVal = (b.name || b.scenarioDescription || '').toLowerCase();
            }
            if (sortOrder === 'desc') {
                return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            }
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        });

        return filtered;
    }, [data, entityType, searchQuery, selectedCategory, selectedIntensities, selectedLenses, sortBy, sortOrder]);

    const allowPaginationSplit = entityType === 'kinks'
        && !searchQuery.trim()
        && !selectedCategory
        && selectedIntensities.length === 0
        && selectedLenses.length === 0
        && sortBy === 'name'
        && sortOrder === 'asc';

    const { popularData, archiveData, shouldSplitKinks, visibleData } = useMemo(() => (
        deriveKinkBrowseSplitState({
            entityType,
            baseFilteredData,
            showNiche,
            showArchive,
            hasMore,
            initialPopularCount,
            allowPaginationSplit
        })
    ), [entityType, baseFilteredData, showNiche, showArchive, hasMore, initialPopularCount, allowPaginationSplit]);

    const revealArchiveWhenBoundaryReached = useCallback(() => {
        if (!scrollContainerRef.current || !popularSectionRef.current || !shouldSplitKinks || showArchive) {
            return false;
        }

        const rootRect = scrollContainerRef.current.getBoundingClientRect();
        const popularRect = popularSectionRef.current.getBoundingClientRect();
        const boundaryIsVisible = popularRect.bottom >= rootRect.top && popularRect.bottom <= rootRect.bottom;
        const boundaryWasPassed = popularRect.bottom <= rootRect.top;

        if (!boundaryIsVisible && !boundaryWasPassed) {
            return false;
        }

        setShowArchive(true);
        return true;
    }, [shouldSplitKinks, showArchive]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (!scrollContainerRef.current || !sentinelRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (shouldLoadNextKinkPage({
                    entry: entries[0],
                    entityType,
                    hasMore,
                    loadingMore,
                    loading
                })) {
                    loadMore();
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '200px 0px'
            }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [entityType, hasMore, loadingMore, loading, loadMore]);

    useEffect(() => {
        if (!scrollContainerRef.current || !archiveRevealRef.current || !popularSectionRef.current || !shouldSplitKinks || showArchive) return;

        if (revealArchiveWhenBoundaryReached()) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (shouldRevealArchiveBoundary({
                    entry: entries[0],
                    shouldSplitKinks,
                    showArchive
                })) {
                    setShowArchive(true);
                    observer.disconnect();
                    return;
                }

                if (revealArchiveWhenBoundaryReached()) observer.disconnect();
            },
            {
                root: scrollContainerRef.current,
                threshold: 0
            }
        );

        observer.observe(archiveRevealRef.current);
        return () => observer.disconnect();
    }, [shouldSplitKinks, showArchive, revealArchiveWhenBoundaryReached]);

    useEffect(() => {
        if (!scrollContainerRef.current || !archiveRevealRef.current || !shouldSplitKinks || showArchive) return;

        const scrollContainer = scrollContainerRef.current;
        const handleScroll = () => {
            revealArchiveWhenBoundaryReached();
        };

        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [shouldSplitKinks, showArchive, revealArchiveWhenBoundaryReached]);

    useEffect(() => {
        if (!scrollContainerRef.current || entityType !== 'kinks' || loading) return;

        const scrollContainer = scrollContainerRef.current;
        const handleScroll = () => {
            const remainingScroll = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
            const reachedBottom = remainingScroll <= 240;

            if (reachedBottom && shouldSplitKinks && !showArchive) {
                setShowArchive(true);
            }

            if (reachedBottom && hasMore && !loadingMore) {
                loadMore();
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [entityType, loading, shouldSplitKinks, showArchive, hasMore, loadingMore, loadMore]);

    const resultsLabel = useMemo(() => {
        const remainingArchiveCount = Math.max(
            archiveData.length,
            totalItems > 0 ? totalItems - popularData.length : 0,
            hasMore ? 1 : 0
        );

        if (shouldSplitKinks && !showArchive) {
            return `${popularData.length} featured now · ${remainingArchiveCount} more to explore`;
        }

        if (shouldSplitKinks && showArchive) {
            return `${baseFilteredData.length} shown · featured first, then full results`;
        }

        const totalCount = totalItems || data.length;
        return visibleData.length === totalCount
            ? `${visibleData.length} shown`
            : `${visibleData.length} shown · ${totalCount} total`;
    }, [shouldSplitKinks, showArchive, popularData.length, archiveData.length, baseFilteredData.length, totalItems, data.length, visibleData.length, hasMore]);

    // Stats for hero (uses totalItems for accurate DB count)
    const stats = config.statsConfig(baseFilteredData, categories, totalItems);

    // Toggle group collapse
    const toggleGroup = (groupName) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupName)) next.delete(groupName);
            else next.add(groupName);
            return next;
        });
    };

    // ========================================================================
    // RENDER LAYOUTS
    // ========================================================================

    // Table layout renderer
    const renderTableLayout = (items = visibleData) => {
        if (!config.columns) return null;

        return (
            <div className="border border-wine-800/40 bg-dark-900/80 overflow-hidden">
                {/* Table Header */}
                <div className="sticky top-0 z-10 bg-dark-950 border-b border-wine-800/50">
                    <div className={`grid gap-4 py-3 px-4 font-bold text-[10px] uppercase tracking-widest text-cream/60`}
                        style={{ gridTemplateColumns: `minmax(250px,1fr) ${config.columns.slice(1).map(() => '100px').join(' ')} 80px` }}
                    >
                        {config.columns.map(col => (
                            <div key={col.key} className={col.type ? 'text-center' : ''}>
                                {col.label}
                            </div>
                        ))}
                        <div className="text-center">View</div>
                    </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-wine-900/10">
                    {items.map((item, idx) => (
                        <Link
                            key={item.id}
                            to={`${config.detailRoute}/${item.id}`}
                            className="grid gap-4 py-3 px-4 hover:bg-wine-950/20 transition-colors"
                            style={{ gridTemplateColumns: `minmax(250px,1fr) ${config.columns.slice(1).map(() => '100px').join(' ')} 80px` }}
                        >
                            {config.columns.map(col => (
                                <div key={col.key} className={col.type ? 'text-center text-cream/70' : 'text-cream/90'}>
                                    {col.key === 'name' ? (
                                        <div>
                                            <div className="font-bold">{item.name || item.scenarioDescription}</div>
                                            {item.summary && (
                                                <div className="text-xs text-cream/40 line-clamp-1">{item.summary}</div>
                                            )}
                                        </div>
                                    ) : col.type === 'scale5' ? (
                                        <span className="text-wine-400">{Math.round((col.transform ? col.transform(item[col.key]) : item[col.key]) / 20) || '-'}/5</span>
                                    ) : col.type === 'skill' ? (
                                        <span className="capitalize text-xs">{item[col.key] || 'beginner'}</span>
                                    ) : (
                                        <span className="text-sm">{item[col.key] || '-'}</span>
                                    )}
                                </div>
                            ))}
                            <div className="text-center">
                                <ChevronRight className="w-4 h-4 text-cream/40 mx-auto" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        );
    };

    // Static Browse Card component - Ultra Premium Redesign (140-char max description)
    const BrowseCard = ({ item }) => {
        // Child variants
        const children = item.childVariants || item.childKinks || [];
        const variantCount = children.length;

        // Numeric stats formatting
        const stats = [];
        if (item.weirdnessScore !== undefined) stats.push({ label: 'Kink', value: `${Math.round(item.weirdnessScore / 20)}/5` });
        if (item.rarity !== undefined) stats.push({ label: 'Rare', value: `${Math.round(item.rarity / 20)}/5` });
        if (item.intensity !== undefined) stats.push({ label: 'Intense', value: `${Math.round((item.intensity * 10) / 20)}/5` });
        if (item.risk !== undefined) stats.push({ label: 'Risk', value: `${Math.round((item.risk * 10) / 20)}/5` });
        if (item.complexity !== undefined) stats.push({ label: 'Complex', value: item.complexity });
        if (item.difficulty !== undefined) stats.push({ label: 'Diff', value: item.difficulty });

        const descText = item.description || item.summary || item.settingDetails;
        const name = item.name || item.scenarioDescription || item.title || `Untitled ${config.label}`;
        const imgUrl = item.imageUrl || item.image;
        const isNiche = item.tier === 'niche';
        const appealProfile = entityType === 'kinks' ? getAppealProfileHighlights(item) : null;
        const primaryAppealLens = appealProfile?.primary;
        const supportingAppealLenses = appealProfile?.supporting || [];
        const appealSummary = entityType === 'kinks' ? describeAppealProfile(item) : null;
        const bgStyle = imgUrl
            ? { backgroundImage: `url(${imgUrl})` }
            : { background: 'linear-gradient(135deg, rgb(48,22,32) 0%, rgb(20,10,15) 50%, rgb(30,15,22) 100%)' };

        return (
            <div className="group relative flex flex-col bg-[#0a0809] border-b border-white/[0.03] sm:border sm:border-white/[0.05] sm:hover:border-wine-500/30 transition-all duration-400 sm:hover:shadow-[0_8px_30px_rgb(0,0,0,0.6)] sm:rounded-lg overflow-hidden sm:bg-gradient-to-b sm:from-white/[0.02] sm:to-transparent">

                {/* ── MOBILE: horizontal compact row (Static) ── */}
                <Link
                    to={`${config.detailRoute}/${item.id}`}
                    className="flex sm:hidden p-3 items-start gap-3 hover:bg-white/[0.02] transition-colors"
                >
                    {/* Contained tactile thumbnail */}
                    <div className="relative w-24 shrink-0 aspect-[5/3] rounded-md overflow-hidden shadow-[0_4px_12px_rgb(0,0,0,0.5)] border border-white/[0.08] mt-1 bg-black">
                        <div className="absolute inset-0 bg-cover bg-center" style={bgStyle} />
                    </div>

                    {/* Highly dense text column */}
                    <div className="flex flex-col min-w-0 flex-1 justify-center pt-1">
                        <div className="min-w-0 pb-0.5">
                            {item.category && (
                                <span className="text-[9px] font-bold uppercase tracking-widest text-wine-400 block mb-0.5 opacity-80">
                                    {item.category}
                                </span>
                            )}
                            <h3 className="font-cinzel font-bold text-[15px] leading-tight text-cream drop-shadow-sm pr-1">
                                {name}
                            </h3>
                        </div>

                        {/* Description */}
                        {descText && (
                            <p className="text-[12px] text-cream/60 leading-snug line-clamp-3 mt-1 pr-2 whitespace-pre-line">
                                {stripMarkdown(descText)}
                            </p>
                        )}

                        {/* Soft architectural stats row */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            {stats.length > 0 && stats.slice(0, 2).map((stat, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1 text-[10px]">
                                    <span className="text-cream/30 uppercase tracking-widest text-[8px] font-bold">{stat.label}</span>
                                    <span className="text-wine-300/90 font-semibold">{stat.value}</span>
                                </span>
                            ))}

                            {/* Variant count pip */}
                            {variantCount > 0 && (
                                <>
                                    <span className="w-1 h-1 rounded-full bg-white/10" />
                                    <span className="text-[9px] uppercase tracking-wider text-wine-300/60 font-medium">
                                        {variantCount} variant{variantCount !== 1 && 's'}
                                    </span>
                                </>
                            )}

                            {[primaryAppealLens, ...supportingAppealLenses].filter(Boolean).map((facet) => (
                                <span key={facet.key} className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${facet.chipClass}`}>
                                    <span>{facet.shortLabel}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </Link>

                {/* ── DESKTOP sm+: vertical card (Static) ── */}
                <div className="hidden sm:flex flex-col flex-1 pb-1">
                    {/* Hero image with title overlay */}
                    <Link to={`${config.detailRoute}/${item.id}`} className="block relative p-2 pb-0">
                        <div className="relative aspect-[4/3] overflow-hidden rounded shadow-inner border border-white/[0.06] bg-black">
                            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100" style={bgStyle} />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0809] via-[#0a0809]/60 via-40% to-black/20" />

                            {/* Top-left: category badge */}
                            {item.category && (
                                <span className="absolute top-2.5 left-2.5 z-10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-wine-950/40 backdrop-blur-md text-cream/90 border border-white/10 rounded-sm shadow-lg shadow-black/50">
                                    {item.category}
                                </span>
                            )}

                            {/* Top-right: stats + niche badge */}
                            <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5">
                                {stats.slice(0, 2).map((stat, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[8px] bg-black/40 backdrop-blur-md border border-white/10 rounded-sm shadow-lg shadow-black/50">
                                        <span className="text-cream/50 uppercase tracking-widest font-bold">{stat.label}</span>
                                        <span className="text-wine-300 font-bold">{stat.value}</span>
                                    </span>
                                ))}
                                {isNiche && (
                                    <span className="px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest bg-purple-950/50 backdrop-blur-md text-purple-300/90 border border-purple-500/20 rounded-sm">
                                        specialized
                                    </span>
                                )}
                            </div>

                            {/* Bottom: title + description overlay on gradient */}
                            <div className="absolute bottom-0 left-0 right-0 z-10 px-3.5 pb-3 pt-10">
                                <h3 className="font-cinzel font-bold text-[17px] leading-snug text-cream group-hover:text-wine-300 transition-colors duration-200 line-clamp-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                    {name}
                                </h3>
                                {descText && (
                                    <p className="text-[11.5px] text-cream/50 leading-relaxed line-clamp-3 mt-1.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                        {stripMarkdown(descText)}
                                    </p>
                                )}
                                {[primaryAppealLens, ...supportingAppealLenses].filter(Boolean).length > 0 && (
                                    <div className="mt-2 space-y-2">
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-cream/35 font-bold">Appeals</div>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {[primaryAppealLens, ...supportingAppealLenses].filter(Boolean).map((facet) => (
                                                <span key={facet.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider backdrop-blur-md ${facet.chipClass}`}>
                                                    <span>{facet.shortLabel}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Link>

                    {/* Card body — related entities */}
                    <div className="flex flex-col flex-1 px-4 pt-3 pb-0 relative">
                        {variantCount > 0 && (
                            <div className="mt-auto mb-4">
                                <div className="h-px w-full bg-gradient-to-r from-wine-800/15 via-wine-800/8 to-transparent mb-2.5" />
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-[8px] uppercase tracking-[0.2em] text-cream/20 font-medium mr-1 self-center">Variations</span>
                                    {children.slice(0, 5).map(child => (
                                        <Link
                                            key={child.id}
                                            to={`${config.detailRoute}/${child.id}`}
                                            className="inline-flex items-center px-2 py-0.5 text-[10px] border border-white/[0.06] hover:border-wine-500/40 hover:bg-wine-950/20 rounded-sm text-cream/35 hover:text-cream/80 transition-all duration-300"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {child.name}
                                        </Link>
                                    ))}
                                    {children.length > 5 && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] text-cream/20">
                                            +{children.length - 5}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {variantCount === 0 && <div className="mb-4" />}
                    </div>
                </div>
            </div>
        );
    };

    // Cards layout: stacked compact rows on mobile, grid on desktop
    const renderCardsLayout = (items = visibleData) => (
        <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 sm:gap-4 lg:gap-5
                        flex flex-col rounded sm:border-0 overflow-hidden sm:overflow-visible">
            {items.map(item => (
                <BrowseCard key={item.id} item={item} />
            ))}
        </div>
    );


    // Grouped cards layout renderer (for Archetypes)
    const renderGroupedCardsLayout = (items = visibleData) => {
        if (!config.categoryGroups) return renderCardsLayout(items);

        // Group items by category
        const groupedItems = {};
        config.categoryGroups.forEach(group => {
            groupedItems[group.name] = items.filter(item =>
                group.categories.includes(item.category?.toLowerCase())
            );
        });

        // Ungrouped items
        const allGroupedCategories = config.categoryGroups.flatMap(g => g.categories);
        const ungrouped = items.filter(item =>
            !allGroupedCategories.includes(item.category?.toLowerCase())
        );

        return (
            <div className="space-y-8">
                {config.categoryGroups.map(group => {
                    const items = groupedItems[group.name];
                    if (items.length === 0) return null;

                    const GroupIcon = group.icon;
                    const isCollapsed = collapsedGroups.has(group.name);

                    return (
                        <div key={group.name} className="space-y-4">
                            {/* Group Header */}
                            <button
                                onClick={() => toggleGroup(group.name)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${group.bgColor} border border-wine-800/30 hover:border-wine-700/50 transition-all`}
                            >
                                <ChevronRight className={`w-5 h-5 ${group.color} transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                                <GroupIcon className={`w-5 h-5 ${group.color}`} />
                                <span className={`font-bold ${group.color}`}>{group.name}</span>
                                <span className="text-sm text-cream/50 ml-auto">{items.length} archetypes</span>
                            </button>

                            {/* Group Items */}
                            {!isCollapsed && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-4">
                                    {items.map(item => (
                                        <ArchetypeCard key={item.id} archetype={item} />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Ungrouped */}
                {ungrouped.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-sm text-cream/50 px-4">Other ({ungrouped.length})</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-4">
                            {ungrouped.map(item => (
                                <ArchetypeCard key={item.id} archetype={item} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Select layout based on config
    const renderLayout = (items = visibleData) => {
        switch (config.layout) {
            case 'table':
                return renderTableLayout(items);
            case 'cards':
                return renderCardsLayout(items);
            case 'groupedCards':
                return renderGroupedCardsLayout(items);
            default:
                return renderTableLayout(items);
        }
    };

    // ========================================================================
    // MAIN RENDER
    // ========================================================================

    return (
        <>
            <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
                {/* Scrollable Content */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto w-full">
                    <main className="max-w-[1800px] w-full mx-auto px-6 py-8">
                        {/* Hero Section */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={entityType}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <DatabaseHero
                                    icon={Icon}
                                    label={config.label}
                                    title={config.title}
                                    highlightWord={config.highlightWord}
                                    description={config.description}
                                    stats={stats}
                                    loading={loading}
                                />
                            </motion.div>
                        </AnimatePresence>

                        {/* Search and Filter Bar */}
                        <div className="mb-4 space-y-3">
                            {/* Main Control Row */}
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Search Input */}
                                <div className="relative group flex-1 min-w-[200px] max-w-md">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40 group-focus-within:text-wine-400 transition-colors" />
                                    <input
                                        type="text"
                                        placeholder={`Search ${config.title.toLowerCase()}...`}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-dark-900/60 border border-wine-800/30 py-2.5 pl-10 pr-4 text-sm text-cream placeholder-cream/30 focus:outline-none focus:border-wine-500/50 transition-colors"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Intensity Visual Dots (inline) */}
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-dark-900/40 border border-wine-800/20">
                                    <span className="text-xs text-cream/40 mr-1">Intensity:</span>
                                    {INTENSITY_LEVELS.map(level => {
                                        const isActive = selectedIntensities.includes(level.key);
                                        const dotColors = {
                                            emerald: 'bg-emerald-500',
                                            amber: 'bg-amber-500',
                                            orange: 'bg-orange-500',
                                            wine: 'bg-wine-500'
                                        };
                                        const ringColors = {
                                            emerald: 'ring-emerald-400',
                                            amber: 'ring-amber-400',
                                            orange: 'ring-orange-400',
                                            wine: 'ring-wine-400'
                                        };
                                        return (
                                            <button
                                                key={level.key}
                                                onClick={() => setSelectedIntensities(prev =>
                                                    isActive
                                                        ? prev.filter(k => k !== level.key)
                                                        : [...prev, level.key]
                                                )}
                                                className={`w-5 h-5 rounded-full ${dotColors[level.color]} transition-all ${isActive ? `ring-2 ${ringColors[level.color]} ring-offset-2 ring-offset-dark-900 scale-110` : 'opacity-40 hover:opacity-70'}`}
                                                title={`${level.label} (${intensityCounts[level.key] || 0})`}
                                            />
                                        );
                                    })}
                                </div>

                                {/* Category Filter Button */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border transition-all ${selectedCategory
                                        ? 'bg-wine-600/20 border-wine-500/50 text-wine-300'
                                        : 'bg-dark-900/60 border-wine-800/30 text-cream/60 hover:text-cream hover:border-wine-600/50'
                                        }`}
                                >
                                    <Filter className="w-3.5 h-3.5" />
                                    {selectedCategory || 'Category'}
                                </button>

                                {entityType === 'kinks' && (
                                    <div className="flex items-center gap-1.5 px-3 py-2 bg-dark-900/40 border border-wine-800/20">
                                        <span className="text-xs text-cream/40 mr-1">Draw:</span>
                                        {APPEAL_FACETS.map((facet) => {
                                            const isActive = selectedLenses.includes(facet.key);
                                            return (
                                                <button
                                                    key={facet.key}
                                                    onClick={() => setSelectedLenses(prev =>
                                                        isActive
                                                            ? prev.filter(key => key !== facet.key)
                                                            : [...prev, facet.key]
                                                    )}
                                                    className={`w-3.5 h-3.5 rounded-full ${facet.colorClass} transition-all ${isActive ? 'ring-2 ring-white/70 ring-offset-2 ring-offset-dark-900 scale-110' : 'opacity-35 hover:opacity-75'}`}
                                                    title={facet.label}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Niche Toggle */}
                                <button
                                    onClick={() => setShowNiche(!showNiche)}
                                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border transition-all ${showNiche
                                        ? 'bg-purple-600/20 border-purple-500/50 text-purple-300'
                                        : 'bg-dark-900/60 border-wine-800/30 text-cream/40 hover:text-cream/60 hover:border-wine-600/50'
                                        }`}
                                    title={showNiche ? 'Showing all entries, including more specialized ones' : 'Showing the most broadly relevant entries first — click to include specialized ones'}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full transition-colors ${showNiche ? 'bg-purple-400' : 'bg-cream/30'}`} />
                                    {showNiche ? 'All' : 'Featured'}
                                </button>

                                {/* Sort Dropdown */}
                                <select
                                    value={`${sortBy}-${sortOrder}`}
                                    onChange={(e) => {
                                        const [field, order] = e.target.value.split('-');
                                        setSortBy(field);
                                        setSortOrder(order);
                                    }}
                                    className="px-3 py-2 text-xs bg-dark-900/60 border border-wine-800/30 text-cream/80 focus:outline-none focus:border-wine-500/50 transition-colors cursor-pointer"
                                >
                                    <option value="name-asc">A → Z</option>
                                    <option value="name-desc">Z → A</option>
                                    <option value="weirdnessScore-desc">Most Intense</option>
                                    <option value="weirdnessScore-asc">Least Intense</option>
                                    <option value="rarity-desc">Rarest</option>
                                    <option value="rarity-asc">Most Common</option>
                                    {entityType === 'kinks' && APPEAL_FACETS.map((facet) => (
                                        <option key={facet.key} value={`${facet.key}-desc`}>Top {facet.label}</option>
                                    ))}
                                </select>

                                {/* Results count */}
                                <span className="text-xs text-cream/40 whitespace-nowrap ml-auto">
                                    {resultsLabel}
                                </span>
                            </div>

                            {/* Multi-Section Filter Panel */}
                            <AnimatePresence>
                                {showFilters && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="p-4 bg-wine-950/30 border border-wine-800/30 space-y-3">
                                            {/* Category Section Only */}
                                            {uniqueCategories.length > 0 ? (
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => setSelectedCategory(null)}
                                                        className={`px-3 py-1.5 text-xs font-medium transition-all ${!selectedCategory
                                                            ? 'bg-wine-600 text-cream border border-wine-500'
                                                            : 'bg-dark-900/60 text-cream/60 border border-wine-800/30 hover:border-wine-600/50 hover:text-cream'
                                                            }`}
                                                    >
                                                        All Categories
                                                    </button>
                                                    {uniqueCategories.map(cat => {
                                                        const count = categoryCounts[cat] || 0;
                                                        const isActive = selectedCategory === cat;
                                                        return (
                                                            <button
                                                                key={cat}
                                                                onClick={() => setSelectedCategory(isActive ? null : cat)}
                                                                className={`px-3 py-1.5 text-xs font-medium transition-all ${isActive
                                                                    ? 'bg-wine-600 text-cream border border-wine-500'
                                                                    : 'bg-dark-900/60 text-cream/60 border border-wine-800/30 hover:border-wine-600/50 hover:text-cream'
                                                                    }`}
                                                            >
                                                                {cat} ({count})
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-cream/40">No categories available</p>
                                            )}

                                            {/* Clear All */}
                                            {selectedCategory && (
                                                <button
                                                    onClick={() => setSelectedCategory(null)}
                                                    className="text-xs text-wine-400 hover:text-wine-300 underline underline-offset-2"
                                                >
                                                    Clear category filter
                                                </button>
                                            )}

                                            {entityType === 'kinks' && (
                                                <>
                                                    <div className="h-px bg-wine-800/20" />
                                                    <div className="space-y-2">
                                                        <div className="text-[10px] uppercase tracking-[0.2em] text-cream/40 font-bold">What draws you in</div>
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => setSelectedLenses([])}
                                                                className={`px-3 py-1.5 text-xs font-medium transition-all ${selectedLenses.length === 0
                                                                    ? 'bg-wine-600 text-cream border border-wine-500'
                                                                    : 'bg-dark-900/60 text-cream/60 border border-wine-800/30 hover:border-wine-600/50 hover:text-cream'
                                                                    }`}
                                                            >
                                                                Any draw
                                                            </button>
                                                            {APPEAL_FACETS.map((facet) => {
                                                                const isActive = selectedLenses.includes(facet.key);
                                                                return (
                                                                    <button
                                                                        key={facet.key}
                                                                        onClick={() => setSelectedLenses(prev =>
                                                                            isActive
                                                                                ? prev.filter(key => key !== facet.key)
                                                                                : [...prev, facet.key]
                                                                        )}
                                                                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border transition-all ${isActive
                                                                            ? facet.chipClass
                                                                            : 'bg-dark-900/60 text-cream/60 border-wine-800/30 hover:border-wine-600/50 hover:text-cream'
                                                                            }`}
                                                                    >
                                                                        <span className={`w-2 h-2 rounded-full ${facet.colorClass}`} />
                                                                         <span>{facet.label}</span>
                                                                     </button>
                                                                 );
                                                             })}
                                                         </div>
                                                        <p className="text-xs text-cream/35 leading-relaxed">
                                                            matches entries where this is one of the main reasons people tend to like it.
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Active Filter Pills (when panel closed) */}
                            {(selectedCategory || selectedIntensities.length > 0 || selectedLenses.length > 0) && !showFilters && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-cream/40">Filters:</span>
                                    {selectedCategory && (
                                        <button
                                            onClick={() => setSelectedCategory(null)}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-wine-600/30 border border-wine-500/40 text-wine-200 text-xs hover:bg-wine-600/50 transition-colors"
                                        >
                                            {selectedCategory}
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                    {selectedIntensities.map(key => {
                                        const level = INTENSITY_LEVELS.find(l => l.key === key);
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setSelectedIntensities(prev => prev.filter(k => k !== key))}
                                                className="flex items-center gap-1.5 px-2 py-1 bg-wine-600/30 border border-wine-500/40 text-wine-200 text-xs hover:bg-wine-600/50 transition-colors"
                                            >
                                                {level?.label}
                                                <X className="w-3 h-3" />
                                            </button>
                                        );
                                    })}
                                    {selectedLenses.map(key => {
                                        const facet = APPEAL_FACETS.find(item => item.key === key);
                                        if (!facet) return null;
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setSelectedLenses(prev => prev.filter(item => item !== key))}
                                                className={`flex items-center gap-1.5 px-2 py-1 border text-xs transition-colors ${facet.chipClass}`}
                                            >
                                                {facet.label}
                                                <X className="w-3 h-3" />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Error State */}
                        {error && (
                            <div className="glass-card p-6 text-center text-red-400 mb-6">
                                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p>{error}</p>
                            </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex items-center justify-center py-20"
                            >
                                <Spinner size="lg" />
                            </motion.div>
                        )}

                        {/* Empty State */}
                        {!loading && !error && visibleData.length === 0 && (
                            <div className="bg-charcoal-950/40 p-20 text-center border border-wine-900/10 rounded-lg">
                                <Icon className="w-12 h-12 mx-auto mb-4 text-wine-900/30" />
                                <h3 className="text-lg font-medium text-cream/60">
                                    {searchQuery ? 'No results found' : `No ${entityType} available`}
                                </h3>
                                <p className="text-sm text-cream/30">
                                    {searchQuery ? 'Try different search terms.' : 'Check back soon!'}
                                </p>
                            </div>
                        )}

                        {/* Content with fade-in */}
                        <AnimatePresence mode="wait">
                            {!loading && !error && visibleData.length > 0 && (
                                <motion.div
                                    key={`${entityType}-content`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    {shouldSplitKinks ? (
                                        <div className="space-y-10">
                                            <div>
                                                <div ref={popularSectionRef}>
                                                    {viewMode === 'card' && config.layout === 'table'
                                                        ? renderCardsLayout(popularData)
                                                        : renderLayout(popularData)}
                                                </div>
                                            </div>

                                            <div ref={archiveRevealRef} className="h-px w-full" aria-hidden="true" />

                                            {showArchive && (
                                                <div className="space-y-6">
                                                    <div className="flex items-center gap-4 pt-2">
                                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-wine-800/40 to-wine-800/10" />
                                                        <div className="text-[10px] uppercase tracking-[0.28em] text-cream/40 whitespace-nowrap">
                                                            more to explore
                                                        </div>
                                                        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-wine-800/40 to-wine-800/10" />
                                                    </div>

                                                    <div className="text-center text-xs text-cream/35">
                                                        broader and more specialized results continue here
                                                    </div>

                                                    {viewMode === 'card' && config.layout === 'table'
                                                        ? renderCardsLayout(archiveData)
                                                        : renderLayout(archiveData)}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        viewMode === 'card' && config.layout === 'table'
                                            ? renderCardsLayout(visibleData)
                                            : renderLayout(visibleData)
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Loading More */}
                        {loadingMore && (
                            <div className="p-4 text-center text-wine-500/50 flex items-center justify-center gap-2">
                                <Spinner size="sm" /> Loading more...
                            </div>
                        )}

                        {/* Sentinel for infinite scroll */}
                        {hasMore && !loading && <div ref={sentinelRef} className="h-10" />}
                    </main>
                </div>
            </div>
        </>
    );
}

export default DatabaseBrowsePage;
